import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Plus, Trash2, CheckCircle2, Circle,
  Clock, Loader2, X, Edit3, ChevronDown, ChevronUp,
  Sparkle, BookOpen, CalendarDays, CalendarRange, Calendar as CalIcon,
} from 'lucide-react';
import BujoPage from './BujoPage';
import BujoSidebar, { BujoPaperBg, useSticker } from './BujoSidebar';
import DayDoodlePad from './DayDoodlePad';
import ThemeStudio from './ThemeStudio';
import MonthlyRecap from './MonthlyRecap';
import WeekTemplatesModal from './WeekTemplatesModal';
import DailyPlanner from './DailyPlanner';
import TaskList from './TaskList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default calendar palette — used when no theme is active
const DEFAULT_CAL_THEME = {
  border_color: '#9CA3AF',
  weekday_bg: '#E4D6F5',
  weekday_text: '#6B5B8C',
  cell_bg: '#ffffff',
  cell_empty_bg: '#FAFAFA',
  today_bg: '#FFF5FA',
  today_accent: '#D06892',
  date_text: '#D8A5C4',
  drag_over_bg: '#FFF0F6',
};

// Build the per-view color palette from an active theme (or fallback to defaults).
// Used by MonthView/MonthCell/WeekView etc so the calendar grid actually changes color.
function calThemeStyle(theme) {
  if (!theme) return { ...DEFAULT_CAL_THEME, template_mode: false };
  const accent = theme.accent || DEFAULT_CAL_THEME.today_accent;
  const accentSoft = theme.accent_soft || DEFAULT_CAL_THEME.date_text;
  const border = theme.border_color || DEFAULT_CAL_THEME.border_color;
  // Pale tint of the soft accent for weekday header bg (just a hint of color)
  const weekdayBg = accentSoft;
  return {
    border_color: border,
    weekday_bg: weekdayBg,
    weekday_text: theme.text_color || DEFAULT_CAL_THEME.weekday_text,
    cell_bg: theme.cell_bg || DEFAULT_CAL_THEME.cell_bg,
    cell_empty_bg: 'rgba(255,255,255,0.55)',
    today_bg: `${accentSoft}33`, // ~20% opacity
    today_accent: accent,
    date_text: accentSoft,
    drag_over_bg: `${accent}1A`,
    // When template_mode is on: image fills grid, cells go transparent
    template_mode: !!theme.template_mode && theme.pattern === 'custom',
  };
}


const MONTHS_SV = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
const WEEKDAYS_SV = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const FREQ_META = {
  daily: { label: 'Dagligt', color: '#B9FBC0' },
  weekly: { label: 'Vecka', color: '#A2D2FF' },
  monthly: { label: 'Månad', color: '#FFC8DD' },
  q3: { label: 'Var 3:e mån', color: '#FFE8A3' },
  q6: { label: 'Var 6:e mån', color: '#CDB4DB' },
  yearly: { label: 'Årligen', color: '#FFBFA3' },
};

// Calendar event category colors (mirrors backend CALENDAR_CATEGORIES)
const CATEGORY_META = {
  work:     { label: 'Jobb',       emoji: '💼', bg: '#D7E9FF', border: '#4A8BC7' },
  personal: { label: 'Personligt', emoji: '💗', bg: '#FFD9E6', border: '#D06892' },
  kids:     { label: 'Barn',       emoji: '🧸', bg: '#FFEFCF', border: '#E0A330' },
  training: { label: 'Träning',    emoji: '🏃', bg: '#D4F7D8', border: '#4DAA4D' },
  health:   { label: 'Hälsa',      emoji: '🏥', bg: '#D9F5E1', border: '#4DAA6B' },
  social:   { label: 'Socialt',    emoji: '🎉', bg: '#FFF4D6', border: '#D6B742' },
  study:    { label: 'Studier',    emoji: '📚', bg: '#E7D9F7', border: '#8B63C4' },
  other:    { label: 'Annat',      emoji: '🌸', bg: '#ECECEC', border: '#888888' },
};

// Chip colors per kind (used in Month/Week views)
const KIND_CHIP = {
  schedule:     { bg: '#FFF4C6', border: '#E6C200', emoji: '⏰' },
  cleaning:     { bg: '#D4F7D8', border: '#4DAA4D', emoji: '🧹' },
  calendar:     { bg: '#CBDEFC', border: '#4A8BC7', emoji: '📅' },
  task:         { bg: '#FFD9E6', border: '#D06892', emoji: '✅' },
  external:     { bg: '#E8E0FF', border: '#7B5FBF', emoji: '🍎' },
  'tpl-schedule': { bg: '#FFE0B2', border: '#FB8C00', emoji: '🕑' },
  'tpl-routine':  { bg: '#E1BEE7', border: '#8E24AA', emoji: '🔁' },
};

// ---- helpers ----
function fmtDateKey(d) {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
function fromDateKey(s) { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); }
function firstOfMonth(y, m) { return new Date(y, m, 1); }
function lastOfMonth(y, m) { return new Date(y, m + 1, 0); }
function isoMonthRange(y, m) {
  return {
    start: new Date(y, m, 1, 0, 0, 0).toISOString(),
    end: new Date(y, m + 1, 0, 23, 59, 59).toISOString(),
  };
}
function dayToKey(d) { return WEEKDAY_KEYS[(d.getDay() + 6) % 7]; }
function isSameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function startOfWeek(d) {
  const day = (d.getDay() + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
}
function timeOf(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Build a flat list of items occurring on a given date.
// Calendar is intentionally minimal — ONLY user-created calendar events.
// Schedules, routines, templates, tasks and cleaning live in their own sections.
function buildDayItems(dateKey, data) {
  const items = [];

  (data.calendar || []).forEach((e) => {
    if (e.occurrence_date === dateKey) {
      items.push({
        id: `cal-${e.event_id}-${dateKey}`, kind: 'calendar',
        time: e.all_day ? null : timeOf(e.starts_at),
        title: e.title, description: e.description,
        category: e.category || 'other',
        completed: !!e.completed, ref_id: e.event_id,
        occurrence_date: dateKey, raw: e,
      });
    }
  });

  // External (read-only) events from synced iCal feeds (Apple/Google/Outlook)
  (data.external || []).forEach((e) => {
    if (e.occurrence_date === dateKey) {
      items.push({
        id: `ext-${e.id}-${dateKey}`, kind: 'external',
        time: e.all_day ? null : timeOf(e.start),
        title: e.title,
        description: e.location ? `📍 ${e.location}` : e.description,
        feed_name: e.feed_name,
        feed_color: e.feed_color || '#7B5FBF',
        all_day: !!e.all_day,
        external: true,
        occurrence_date: dateKey,
      });
    }
  });

  items.sort((a, b) => {
    const ta = a.time || '99:99'; const tb = b.time || '99:99';
    if (ta !== tb) return ta.localeCompare(tb);
    return (a.title || '').localeCompare(b.title || '');
  });
  return items;
}

// BuJo symbol mapping — visual cues for the calendar
const BUJO_SYMBOLS = {
  event:     { glyph: '▲', label: 'Händelse', desc: 'Något som händer' },
  task:      { glyph: '☐', label: 'Att göra', desc: 'En sak att kryssa av' },
  note:      { glyph: '•', label: 'Anteckning', desc: 'Tanke att minnas' },
};

// ---- Event Editor modal ----
function EventEditor({ date, existing, onSave, onClose }) {
  const [eventDate, setEventDate] = useState(() => existing?.starts_at ? fmtDateKey(new Date(existing.starts_at)) : date);
  const [title, setTitle] = useState(existing?.title || '');
  const [time, setTime] = useState(() => existing?.starts_at ? new Date(existing.starts_at).toTimeString().slice(0, 5) : '09:00');
  const [allDay, setAllDay] = useState(existing?.all_day || false);
  const [category, setCategory] = useState(existing?.category || 'personal');
  const [recurrence, setRecurrence] = useState(existing?.recurrence || 'none');
  const [important, setImportant] = useState(existing?.important || false);
  const [bujoKind, setBujoKind] = useState(existing?.bujo_kind || 'event');
  const [inPaper, setInPaper] = useState(existing?.in_paper !== false);
  const [autoRem, setAutoRem] = useState(existing ? !!existing.auto_reminders : true);
  const [reminders, setReminders] = useState(() => {
    if (existing?.reminders && Array.isArray(existing.reminders) && existing.reminders.length) return new Set(existing.reminders);
    if (existing?.reminder_minutes != null) return new Set([existing.reminder_minutes]);
    if (existing?.event_id) return new Set();
    // New events get the automatic 1 week / 1 day / same-morning reminders instead
    return new Set();
  });
  const [busy, setBusy] = useState(false);

  const toggleReminder = (m) => {
    setReminders((prev) => {
      const n = new Set(prev);
      if (n.has(m)) n.delete(m); else n.add(m);
      return n;
    });
  };

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!title.trim()) {
      toast.error('Skriv en titel först 🌸');
      return;
    }
    if (!eventDate) {
      toast.error('Välj ett datum');
      return;
    }
    setBusy(true);
    try {
      const starts_at = allDay ? `${eventDate}T00:00:00` : new Date(`${eventDate}T${time || '09:00'}:00`).toISOString();
      const remList = Array.from(reminders).sort((a, b) => b - a);
      const payload = {
        title: title.trim(), starts_at, all_day: allDay, category, recurrence,
        reminders: remList,
        reminder_minutes: remList.length ? remList[remList.length - 1] : null,
        important,
        bujo_kind: bujoKind,
        in_paper: inPaper,
        auto_reminders: autoRem,
      };
      if (existing?.event_id) {
        await axios.patch(`${API}/calendar/events/${existing.event_id}`, payload, { withCredentials: true });
        toast.success('Uppdaterad 💗');
      } else {
        await axios.post(`${API}/calendar/events`, payload, { withCredentials: true });
        toast.success('Tillagd i kalendern ✨');
      }
      onSave(); onClose();
    } catch (err) {
       
      console.error('Calendar save failed:', err?.response?.status, err?.response?.data, err);
      const detail = err?.response?.data?.detail || err?.message || 'okänt fel';
      toast.error(`Kunde inte spara: ${detail}`);
    }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!existing?.event_id) return;
    if (!window.confirm('Ta bort händelsen?')) return;
    try {
      await axios.delete(`${API}/calendar/events/${existing.event_id}`, { withCredentials: true });
      toast.success('Borttagen'); onSave(); onClose();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="event-editor-backdrop">
      <form
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="neo-card bg-white p-4 w-full max-w-md space-y-3 my-2 sm:my-0 max-h-[96vh] overflow-y-auto"
        data-testid="event-editor"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{existing ? 'Ändra händelse' : 'Ny händelse'}</h3>
          <button type="button" onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
        </div>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel" className="neo-input w-full" autoFocus data-testid="event-title" />

        {/* BuJo type picker */}
        <div data-testid="event-bujo-picker">
          <p className="text-[11px] font-bold uppercase opacity-60 mb-1">Typ</p>
          <div className="grid grid-cols-3 gap-1">
            {Object.entries(BUJO_SYMBOLS).map(([k, v]) => (
              <button
                type="button"
                key={k}
                onClick={() => setBujoKind(k)}
                className={`rounded-md p-2 border-2 text-[10px] font-bold flex flex-col items-center gap-0.5 transition-all ${bujoKind === k ? 'bg-[#FFE8F5] border-[#D06892] ring-2 ring-[#D06892]/40' : 'bg-white border-black/10 opacity-80 hover:opacity-100'}`}
                data-testid={`event-bujo-${k}`}
                title={v.desc}
              >
                <span className="text-lg leading-none">{v.glyph}</span>
                <span>{v.label}</span>
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm bg-[#F4EEE1] rounded-md px-2 py-1.5 border-l-4 border-[#6B7246] cursor-pointer" data-testid="event-in-paper-label">
          <input type="checkbox" checked={inPaper} onChange={(e) => setInPaper(e.target.checked)} data-testid="event-in-paper" className="w-4 h-4" />
          <span>📰 Med i dagstidningen</span>
        </label>
        <label className="flex items-center gap-2 text-sm bg-[#F5EEFF] rounded-md px-2 py-1.5 border-l-4 border-[#C8B6FF] cursor-pointer" data-testid="event-auto-rem-label">
          <input type="checkbox" checked={autoRem} onChange={(e) => setAutoRem(e.target.checked)} data-testid="event-auto-reminders" className="w-4 h-4" />
          <span>🔔 Påminn 1 vecka innan, 1 dag innan och samma morgon</span>
        </label>
        {/* Important toggle */}
        <label className="flex items-center gap-2 text-sm bg-[#FFF8E1] rounded-md px-2 py-1.5 border-l-4 border-[#E0A330] cursor-pointer" data-testid="event-important-label">
          <input
            type="checkbox"
            checked={important}
            onChange={(e) => setImportant(e.target.checked)}
            data-testid="event-important"
            className="w-4 h-4"
          />
          <span className="font-bold">!</span>
          <span>Viktig — visa i "Kommande 30 dagar"</span>
        </label>
        <div>
          <p className="text-[11px] font-bold uppercase opacity-60 mb-1">Datum</p>
          <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="neo-input w-full" data-testid="event-date" />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> Hela dagen</label>
        {!allDay && <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="neo-input w-full" data-testid="event-time" />}
        <div>
          <p className="text-[11px] font-bold uppercase opacity-60 mb-1">Kategori</p>
          <div className="grid grid-cols-4 gap-1" data-testid="event-category-picker">
            {Object.entries(CATEGORY_META).map(([k, v]) => (
              <button
                type="button"
                key={k}
                onClick={() => setCategory(k)}
                className={`rounded-md p-1.5 border-2 text-[10px] font-bold flex flex-col items-center transition-all ${category === k ? 'ring-2 ring-[#D06892]' : ''}`}
                style={{ backgroundColor: v.bg, borderColor: v.border }}
                data-testid={`event-cat-${k}`}
                title={v.label}
              >
                <span className="text-base leading-none">{v.emoji}</span>
                <span className="mt-0.5 leading-tight truncate w-full text-center">{v.label}</span>
              </button>
            ))}
          </div>
        </div>
        <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="neo-input w-full">
          <option value="none">Engångs</option><option value="daily">Varje dag</option><option value="weekly">Varje vecka</option><option value="monthly">Varje månad</option><option value="yearly">Varje år</option>
        </select>
        <div>
          <p className="text-[11px] font-bold uppercase opacity-60 mb-1">Påminnelser</p>
          <div className="grid grid-cols-3 gap-1" data-testid="event-reminders-picker">
            {[
              { m: 0, label: 'Vid start' },
              { m: 5, label: '5 min' },
              { m: 15, label: '15 min' },
              { m: 30, label: '30 min' },
              { m: 60, label: '1 tim' },
              { m: 1440, label: '1 dag' },
              { m: 10080, label: '1 vecka' },
            ].map(({ m, label }) => (
              <button
                type="button"
                key={m}
                onClick={() => toggleReminder(m)}
                className={`text-[10px] font-bold px-2 py-1 rounded border-2 transition-all ${reminders.has(m) ? 'bg-[#FFC8DD] border-[#D06892] ring-2 ring-[#D06892]/40' : 'bg-white border-black/15 opacity-70 hover:opacity-100'}`}
                data-testid={`event-reminder-${m}`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-[10px] opacity-60 mt-1">Tipsar dig så du hinner förbereda 💛</p>
        </div>
        <div className="flex gap-2 sticky bottom-0 -mx-4 px-4 -mb-4 pb-4 pt-2 bg-white border-t border-black/5">
          <button type="submit" disabled={busy} className="neo-btn flex-1 py-3 bg-[#B9FBC0] font-bold text-base" data-testid="event-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Spara ✨'}
          </button>
          {existing && <button type="button" onClick={del} className="neo-btn px-3 py-3 bg-red-100" data-testid="event-delete"><Trash2 className="w-4 h-4 text-red-600" /></button>}
        </div>
      </form>
    </div>
  );
}

// ---- QuickAdd modal (task or schedule) ----
function QuickAddMenu({ date, onAdded, onClose }) {
  const [mode, setMode] = useState('menu');
  const [title, setTitle] = useState('');
  const [qaDate, setQaDate] = useState(date);
  const [time, setTime] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); if (!title.trim()) return;
    setBusy(true);
    try {
      if (mode === 'task') {
        await axios.post(`${API}/tasks`, { title: title.trim(), priority: 'medium', due_date: `${qaDate}T12:00:00.000Z`, points: 10 }, { withCredentials: true });
        toast.success('✅ Task skapad');
      } else if (mode === 'schedule') {
        await axios.post(`${API}/schedule`, { title: title.trim(), scheduled_time: time || '09:00', days: [dayToKey(fromDateKey(qaDate))], is_blocking: blocking, category: 'custom', points: 10 }, { withCredentials: true });
        toast.success(blocking ? '🔒 Tids-påminnelse skapad' : '⏰ Påminnelse skapad');
      }
      onAdded(); onClose();
    } catch { toast.error('Kunde inte skapa'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="quickadd-backdrop">
      <div className="neo-card bg-white p-4 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()} data-testid="quickadd-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">Lägg till</h3>
          <button onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
        </div>
        {mode === 'menu' && (
          <div className="grid grid-cols-1 gap-2">
            <button onClick={() => setMode('task')} className="neo-btn p-3 bg-[#FFC8DD] text-left font-bold" data-testid="quickadd-choose-task">✅ Task — bara bocka av</button>
            <button onClick={() => setMode('schedule')} className="neo-btn p-3 bg-[#FFE8A3] text-left font-bold" data-testid="quickadd-choose-schedule">🔔 Tids-påminnelse — med klockslag</button>
          </div>
        )}
        {mode !== 'menu' && (
          <form onSubmit={submit} className="space-y-3">
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Vad ska göras?" className="neo-input w-full" autoFocus data-testid="quickadd-title" />
            <div>
              <p className="text-[11px] font-bold uppercase opacity-60 mb-1">Datum</p>
              <input type="date" value={qaDate} onChange={(e) => setQaDate(e.target.value)} className="neo-input w-full" data-testid="quickadd-date" />
            </div>
            {mode === 'schedule' && (<>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="neo-input w-full" data-testid="quickadd-time" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={blocking} onChange={(e) => setBlocking(e.target.checked)} data-testid="quickadd-blocking" /> 🔒 Låser appen tills jag bockar av</label>
            </>)}
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode('menu')} className="neo-btn px-3 py-2 bg-gray-200 text-sm">← Tillbaka</button>
              <button type="submit" disabled={busy || !title.trim()} className="neo-btn flex-1 py-2 bg-[#B9FBC0] font-bold" data-testid="quickadd-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Spara'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---- Day Detail Modal (opens from Month/Week cell click) ----
function DayDetailModal({ date, data, onClose, onRefresh, bujoMode }) {
  const items = useMemo(() => buildDayItems(date, data), [date, data]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const d = fromDateKey(date);
  const label = d.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const open = items.filter((i) => !i.completed);
  const done = items.filter((i) => i.completed);

  const toggle = async (item) => {
    try {
      if (item.kind === 'schedule') {
        await axios.patch(`${API}/schedule/${item.ref_id}`, { completed_today: !item.completed }, { withCredentials: true });
      } else if (item.kind === 'cleaning') {
        await axios.post(`${API}/cleaning/tasks/${item.ref_id}/${item.completed ? 'uncomplete' : 'complete'}`, {}, { withCredentials: true });
      } else if (item.kind === 'calendar') {
        const url = item.completed ? `${API}/calendar/events/uncomplete` : `${API}/calendar/events/complete`;
        await axios.post(url, { event_id: item.ref_id, occurrence_date: item.occurrence_date }, { withCredentials: true });
      } else if (item.kind === 'task') {
        await axios.patch(`${API}/tasks/${item.ref_id}`, { completed: !item.completed }, { withCredentials: true });
      }
      onRefresh();
    } catch { toast.error('Kunde inte uppdatera'); }
  };

  const removeTask = async (item) => {
    if (!window.confirm('Ta bort denna task?')) return;
    try { await axios.delete(`${API}/tasks/${item.ref_id}`, { withCredentials: true }); toast.success('Borttagen'); onRefresh(); }
    catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="day-detail-backdrop">
      <div className="neo-card bg-gradient-to-br from-[#FFF7E6] via-[#FFF0F6] to-[#F5EEFF] w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="day-detail-modal">
        <header className="sticky top-0 z-10 bg-gradient-to-br from-[#FFE8A3] via-[#FFC8DD] to-[#C8B6FF] p-3 flex items-center justify-between border-b-2 border-black/10">
          <div>
            <p className="text-[11px] font-bold uppercase opacity-60">Dag</p>
            <h3 className="font-['Fredoka'] font-bold text-lg capitalize leading-tight">{label}</h3>
          </div>
          <button onClick={onClose} className="p-1" data-testid="day-detail-close"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-3 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setEditingEvent({})}
              className="neo-btn flex-1 sm:flex-none px-4 py-2.5 bg-gradient-to-br from-[#FFC8DD] to-[#FFE8F5] text-sm font-bold flex items-center justify-center gap-1.5 min-h-[44px]"
              data-testid="day-add-event"
            >
              <Plus className="w-4 h-4" /> Lägg till händelse
            </button>
            <button onClick={() => setShowQuickAdd(true)} className="neo-btn px-3 py-2 bg-white text-sm font-bold flex items-center gap-1 min-h-[44px]" data-testid="day-add-task"><Plus className="w-4 h-4" /> Task / Tid</button>
          </div>
          {items.length === 0 ? (
            <div className="neo-card p-4 bg-white/80 text-center"><p className="text-sm font-bold">🌸 Inget planerat</p><p className="text-xs opacity-60 mt-1">Lägg till en task eller händelse</p></div>
          ) : (
            <>
              <ul className="space-y-2" data-testid="day-open-list">
                {open.map((it) => <DayItem key={it.id} item={it} onToggle={toggle} onDelete={removeTask} onEditEvent={setEditingEvent} bujoMode={bujoMode} />)}
              </ul>
              {done.length > 0 && (
                <details className="neo-card p-2 bg-white/70" data-testid="day-done-section">
                  <summary className="cursor-pointer text-xs font-bold opacity-70 px-2 py-1">✅ Klara ({done.length})</summary>
                  <ul className="space-y-2 mt-2">{done.map((it) => <DayItem key={it.id} item={it} onToggle={toggle} onDelete={removeTask} onEditEvent={setEditingEvent} bujoMode={bujoMode} />)}</ul>
                </details>
              )}
            </>
          )}

          {/* Doodle-pad: lets the user draw a small sketch directly on the expanded day */}
          <details className="neo-card bg-gradient-to-br from-[#F5F0FF] to-[#FFFBFF] border-violet-200" data-testid="day-doodle-section">
            <summary className="cursor-pointer text-xs font-bold px-3 py-2 text-violet-800 flex items-center gap-1.5">
              🎨 Doodle på denna dag
              <span className="text-[10px] opacity-60 italic font-normal ml-auto">rita / klotter / kawaii-skiss</span>
            </summary>
            <div className="px-2 pb-2 pt-1">
              <DayDoodlePad date={date} />
            </div>
          </details>
        </div>
      </div>
      {editingEvent && <EventEditor date={date} existing={editingEvent.event_id ? editingEvent : null} onSave={onRefresh} onClose={() => setEditingEvent(null)} />}
      {showQuickAdd && <QuickAddMenu date={date} onAdded={onRefresh} onClose={() => setShowQuickAdd(false)} />}
    </div>
  );
}

function DayItem({ item, onToggle, onDelete, onEditEvent, bujoMode }) {
  const [busy, setBusy] = useState(false);
  const kindChip = KIND_CHIP[item.kind] || {};
  const catMeta = item.kind === 'calendar' ? (CATEGORY_META[item.category] || CATEGORY_META.other) : null;
  const bg = item.kind === 'external' ? (item.feed_color || kindChip.bg) + '33' : (catMeta?.bg || kindChip.bg);
  const emoji = catMeta?.emoji || kindChip.emoji;
  const toggle = async () => { setBusy(true); await onToggle(item); setBusy(false); };
  return (
    <li className={`neo-card p-3 flex items-start gap-3 ${item.completed ? 'opacity-60' : ''}`} style={{ backgroundColor: bg, borderLeft: item.kind === 'external' ? `5px solid ${item.feed_color || '#7B5FBF'}` : undefined }} data-testid={`day-item-${item.id}`}>
      {item.kind === 'external' ? (
        <span className="shrink-0 mt-0.5 text-xl" title="Synkad från extern kalender (read-only)">🍎</span>
      ) : (
        <button onClick={toggle} disabled={busy} className="shrink-0 mt-0.5" data-testid={`day-toggle-${item.id}`}>
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : item.completed ? <CheckCircle2 className="w-6 h-6 text-green-600 fill-green-200" /> : <Circle className="w-6 h-6 opacity-60" />}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          {bujoMode && <Sticker title={item.title} />}
          {item.kind !== 'external' && <span className="text-lg shrink-0">{emoji}</span>}
          <h4 className={`font-bold text-sm leading-tight flex-1 ${item.completed ? 'line-through' : ''}`}>{item.title}</h4>
          {item.time && <span className="text-xs font-bold opacity-70 flex items-center gap-0.5"><Clock className="w-3 h-3" /> {item.time}</span>}
        </div>
        {item.description && <p className="text-[11px] opacity-70 mt-0.5">{item.description}</p>}
        <div className="flex items-center gap-2 mt-1">
          {item.is_blocking && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-red-400 bg-white text-red-600">🔒 Låser appen</span>}
          {catMeta && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'white', borderLeft: `3px solid ${catMeta.border}` }}>{catMeta.label}</span>}
          {item.kind === 'external' && item.feed_name && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white" style={{ borderLeft: `3px solid ${item.feed_color || '#7B5FBF'}` }}>{item.feed_name}</span>
          )}
          {item.points > 0 && <span className="text-[10px] font-bold opacity-60">+{item.points}p</span>}
          <span className="text-[9px] uppercase opacity-50 ml-auto font-bold">{
            item.kind === 'schedule' ? 'Tid'
            : item.kind === 'cleaning' ? 'Städ'
            : item.kind === 'calendar' ? 'Kalender'
            : item.kind === 'external' ? 'Extern'
            : item.kind === 'tpl-schedule' ? 'Daglig tid'
            : item.kind === 'tpl-routine' ? 'Rutin'
            : 'Task'
          }</span>
        </div>
      </div>
      {item.kind === 'task' && <button onClick={() => onDelete(item)} className="shrink-0 opacity-50 hover:opacity-100 p-1" data-testid={`day-delete-${item.id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>}
      {item.kind === 'calendar' && item.raw && <button onClick={() => onEditEvent(item.raw)} className="shrink-0 opacity-50 hover:opacity-100 p-1" data-testid={`day-edit-${item.id}`}><Edit3 className="w-3.5 h-3.5" /></button>}
    </li>
  );
}

// ---- Inline chip for Month/Week views ----
function InlineChip({ item, bujoMode }) {
  const kindChip = KIND_CHIP[item.kind] || {};
  const catMeta = item.kind === 'calendar' ? (CATEGORY_META[item.category] || CATEGORY_META.other) : null;
  const isExternal = item.kind === 'external';
  const bg = isExternal ? (item.feed_color || kindChip.bg) + '33' : (catMeta?.bg || kindChip.bg);
  const border = isExternal ? (item.feed_color || kindChip.border) : (catMeta?.border || kindChip.border);
  const emoji = isExternal ? '🍎' : (catMeta?.emoji || kindChip.emoji);
  return (
    <div
      className={`truncate text-[10px] leading-tight px-1 py-0.5 rounded border-l-2 ${item.completed ? 'line-through opacity-50' : ''}`}
      style={{ backgroundColor: bg, borderLeftColor: border }}
      title={`${item.time ? item.time + ' · ' : ''}${item.title}${catMeta ? ' · ' + catMeta.label : ''}${isExternal && item.feed_name ? ' · ' + item.feed_name : ''}`}
      data-testid={`chip-${item.id}`}
    >
      {item.time && <span className="font-bold mr-1">{item.time}</span>}
      {bujoMode ? <Sticker title={item.title} /> : ((item.kind === 'calendar' || isExternal) && <span className="mr-0.5">{emoji}</span>)}
      <span>{item.title}</span>
    </div>
  );
}

// ---- MONTH VIEW ----
// Tiny inline component to render the auto-sticker for an event title (used in bujo-mode)
function Sticker({ title }) {
  const e = useSticker(title);
  return <span className="mr-0.5" aria-hidden>{e || '✨'}</span>;
}

function MonthCell({ cell, data, isFirstCol, isLastRow, isToday, onDayClick, onCreated, onDropEvent, draggingId, bujoMode, themeStyle, cellMinH }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const key = fmtDateKey(cell.date);
  const items = buildDayItems(key, data);
  const calendarItems = items.filter((i) => i.kind === 'calendar');
  const categories = Array.from(new Set(items.map((i) => {
    if (i.kind === 'calendar') return `cat-${i.category || 'other'}`;
    return `kind-${i.kind}`;
  }))).slice(0, 4);

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const t = (text || '').trim();
    if (!t) { setAdding(false); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/calendar/events`, {
        title: t,
        starts_at: `${key}T09:00:00`,
        all_day: true,
        category: 'personal',
        recurrence: 'none',
        reminders: [10080, 1440],
        bujo_kind: 'event',
        important: false,
      }, { withCredentials: true });
      toast.success('Tillagd ✨');
      setText('');
      setAdding(false);
      onCreated();
    } catch (err) {
       
      console.error('Quick-add failed:', err?.response?.status, err?.response?.data, err);
      const detail = err?.response?.data?.detail || err?.message || 'okänt fel';
      toast.error(`Kunde inte lägga till: ${detail}`);
    }
    finally { setBusy(false); }
  };

  const handleDragStart = (e, item) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-event', JSON.stringify({
      event_id: item.ref_id,
      from_date: key,
      starts_at: item.raw?.starts_at,
      all_day: item.raw?.all_day,
    }));
    onDropEvent?.(null, { dragging: item.ref_id });
  };
  const handleDragEnd = () => onDropEvent?.(null, { dragging: null });

  const handleDragOver = (e) => {
    if (e.dataTransfer.types.includes('application/x-event')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!dragOver) setDragOver(true);
    }
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    try {
      const raw = e.dataTransfer.getData('application/x-event');
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data.from_date === key) return; // same day, no-op
      onDropEvent?.(key, data);
    } catch { /* dropped invalid drag payload */ }
  };

  const ts = themeStyle || DEFAULT_CAL_THEME;
  const tplMode = !!ts.template_mode;
  const minH = cellMinH || 110;
  return (
    <div
      className={`group relative flex flex-col items-stretch text-left p-1.5 transition-all ${tplMode ? '' : `${isFirstCol ? '' : 'border-l'} ${isLastRow ? '' : 'border-b'}`} cursor-pointer`}
      style={{
        minHeight: minH,
        borderColor: tplMode ? 'transparent' : ts.border_color,
        backgroundColor: tplMode
          ? (dragOver ? ts.drag_over_bg : 'transparent')
          : (dragOver ? ts.drag_over_bg : (isToday ? ts.today_bg : ts.cell_bg)),
      }}
      onClick={() => !adding && onDayClick(key)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={`month-day-${key}`}
      data-drag-target={dragOver ? 'true' : 'false'}
    >
      <div className="flex items-start justify-between">
        <button
          onClick={(e) => {
            e.stopPropagation();
            // On small (mobile) screens, the inline input is too cramped → open the full modal instead.
            if (typeof window !== 'undefined' && window.innerWidth < 640) {
              onDayClick(key);
            } else {
              setAdding(true);
            }
          }}
          className="text-[14px] leading-none w-7 h-7 rounded-full opacity-60 hover:opacity-100 hover:bg-white/60 active:bg-white/60 transition-all flex items-center justify-center"
          title="Lägg till händelse"
          data-testid={`month-quickwrite-${key}`}
          aria-label="Lägg till händelse på denna dag"
        >
          <Plus className="w-4 h-4" />
        </button>
        <span
          className={`text-sm font-bold leading-none ${isToday ? 'w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0' : ''}`}
          style={{
            color: isToday ? 'white' : ts.date_text,
            backgroundColor: isToday ? ts.today_accent : 'transparent',
          }}
        >
          {cell.date.getDate()}
        </span>
      </div>
      {adding && (
        <form
          onSubmit={submit}
          onClick={(e) => e.stopPropagation()}
          className="mt-1"
          data-testid={`month-inline-form-${key}`}
        >
          <input
            type="text"
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => {
              // On blur, just close the inline input WITHOUT submitting (prevents accidental save with partial text)
              if (!text.trim()) setAdding(false);
            }}
            onKeyDown={(e) => { if (e.key === 'Escape') { setAdding(false); setText(''); } }}
            placeholder="Skriv & tryck Enter…"
            className="w-full text-[10px] px-1 py-0.5 rounded border border-[#D06892] bg-[#FFF8FA] outline-none focus:ring-1 focus:ring-[#D06892]"
            disabled={busy}
            data-testid={`month-inline-input-${key}`}
          />
        </form>
      )}
      {/* Show up to 2 items with text — works for calendar events, schedules, cleanings and tasks alike */}
      {items.length > 0 && !adding && (
        <div className="mt-1 space-y-0.5">
          {items.slice(0, 2).map((it) => {
            let bg = '#F3F4F6';
            let border = '#9CA3AF';
            const cat = it.kind === 'calendar' ? (CATEGORY_META[it.category] || CATEGORY_META.other) : null;
            if (cat) { bg = cat.bg; border = cat.border; }
            const bujoGlyph = it.kind === 'calendar' ? (BUJO_SYMBOLS[it.bujo_kind || 'event']?.glyph || '▲') : '•';
            const importantMark = it.kind === 'calendar' && it.important ? '!' : '';
            const isDraggable = it.kind === 'calendar';
            return (
              <div
                key={it.id}
                draggable={isDraggable}
                onClick={(e) => e.stopPropagation()}
                onDragStart={isDraggable ? (e) => handleDragStart(e, it) : undefined}
                onDragEnd={isDraggable ? handleDragEnd : undefined}
                className={`text-[9px] truncate px-1 py-[1px] rounded border-l-2 ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''} ${draggingId === it.ref_id ? 'opacity-40' : ''}`}
                style={{ backgroundColor: bg, borderLeftColor: border }}
                title={it.title}
                data-testid={`month-item-${it.kind}-${it.ref_id || it.id}-${key}`}
              >
                <span className="font-bold">{importantMark}{bujoGlyph}</span>{bujoMode && ' '}{bujoMode && <Sticker title={it.title} />} {it.title}
              </div>
            );
          })}
          {items.length > 2 && <div className="text-[8px] opacity-50 font-bold">+{items.length - 2}</div>}
        </div>
      )}
    </div>
  );
}

function MonthView({ year, month, data, onDayClick, onCreated, bujoMode, themeStyle, spacious }) {
  const first = firstOfMonth(year, month);
  const last = lastOfMonth(year, month);
  const firstWeekday = (first.getDay() + 6) % 7;
  const daysInMonth = last.getDate();
  const totalCells = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - firstWeekday + 1;
    if (dayNum < 1 || dayNum > daysInMonth) cells.push({ date: null, dayNum });
    else cells.push({ date: new Date(year, month, dayNum), dayNum });
  }

  const [draggingId, setDraggingId] = useState(null);

  const handleDropOrDragState = async (toDateKey, payload) => {
    // payload {dragging: id} or actual move {event_id, from_date, starts_at, all_day}
    if (toDateKey === null) {
      setDraggingId(payload?.dragging || null);
      return;
    }
    if (!payload?.event_id) return;
    try {
      // Compose new starts_at: keep time-of-day if not all-day
      let newStartsAt = `${toDateKey}T09:00:00`;
      if (!payload.all_day && payload.starts_at) {
        const t = new Date(payload.starts_at);
        newStartsAt = new Date(`${toDateKey}T${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}:00`).toISOString();
      } else if (payload.all_day) {
        newStartsAt = `${toDateKey}T00:00:00`;
      }
      await axios.patch(`${API}/calendar/events/${payload.event_id}`, { starts_at: newStartsAt }, { withCredentials: true });
      toast.success('📅 Flyttat ✨');
      onCreated?.();
    } catch { toast.error('Kunde inte flytta'); }
    finally { setDraggingId(null); }
  };

  const today = new Date();
  const ts = themeStyle || DEFAULT_CAL_THEME;
  const tplMode = !!ts.template_mode;
  const cellMinH = spacious ? 220 : 110;
  const emptyMinH = spacious ? 180 : 90;
  // In template mode the grid is fully transparent so the bg image shines through.
  const outerBorder = tplMode ? 'none' : `1px solid ${ts.border_color}`;
  const outerBg = tplMode ? 'transparent' : ts.cell_bg;
  return (
    <div className="p-3 rounded-md" data-testid="month-view" style={{ border: outerBorder, backgroundColor: outerBg }}>
      <div className="mb-3 flex items-baseline gap-3">
        <span className="text-[10px] font-bold tracking-[0.3em] opacity-50 uppercase">Månad</span>
        <h2 className="font-['Fredoka'] text-2xl font-bold capitalize pb-1" style={{ borderBottom: `2px solid ${ts.today_accent}` }}>
          {MONTHS_SV[month]} {year}
        </h2>
        <span className="text-[10px] opacity-50 ml-auto italic">Hovra cellen → klicka + · Dra händelse för att flytta</span>
      </div>
      {!tplMode && (
        <div className="grid grid-cols-7" style={{ border: `1px solid ${ts.border_color}`, borderBottom: 'none' }}>
          {WEEKDAYS_SV.map((w, i) => (
            <div
              key={w}
              className={`text-center text-[11px] font-bold py-1.5 uppercase tracking-widest ${i > 0 ? 'border-l' : ''}`}
              style={{ backgroundColor: ts.weekday_bg, color: ts.weekday_text, borderLeftColor: ts.border_color }}
            >
              {w}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-7" style={{ border: tplMode ? 'none' : `1px solid ${ts.border_color}` }}>
        {cells.map((cell, idx) => {
          const isFirstCol = idx % 7 === 0;
          const isLastRow = idx >= cells.length - 7;
          if (!cell.date) {
            return (
              <div
                key={idx}
                className={tplMode ? '' : `${isFirstCol ? '' : 'border-l'} ${isLastRow ? '' : 'border-b'}`}
                style={{ minHeight: emptyMinH, borderColor: ts.border_color, backgroundColor: tplMode ? 'transparent' : ts.cell_empty_bg }}
              />
            );
          }
          return (
            <MonthCell
              key={fmtDateKey(cell.date)}
              cell={cell}
              data={data}
              isFirstCol={isFirstCol}
              isLastRow={isLastRow}
              isToday={isSameDay(cell.date, today)}
              onDayClick={onDayClick}
              onCreated={onCreated}
              onDropEvent={handleDropOrDragState}
              draggingId={draggingId}
              bujoMode={bujoMode}
              themeStyle={ts}
              cellMinH={cellMinH}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---- WEEK VIEW ----
function WeekView({ anchor, data, onDayClick, bujoMode }) {
  const weekStart = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
  const today = new Date();

  // Aggregate all items for the whole week
  const weekItems = useMemo(() => {
    const all = [];
    days.forEach((d) => {
      const k = fmtDateKey(d);
      buildDayItems(k, data).forEach((it) => {
        all.push({ ...it, dateKey: k, weekday: d });
      });
    });
    all.sort((a, b) => {
      if (a.dateKey !== b.dateKey) return a.dateKey.localeCompare(b.dateKey);
      return (a.time || '99:99').localeCompare(b.time || '99:99');
    });
    return all;
  }, [data, days]);

  const we = new Date(weekStart); we.setDate(weekStart.getDate() + 6);
  const weekRange = `${weekStart.getDate()} ${MONTHS_SV[weekStart.getMonth()].slice(0, 3).toLowerCase()} – ${we.getDate()} ${MONTHS_SV[we.getMonth()].slice(0, 3).toLowerCase()}`;

  return (
    <div className="space-y-3" data-testid="week-view-wrap">
      {/* Hero: Denna vecka */}
      <section className="neo-card p-3 bg-gradient-to-br from-[#FFF7E6] via-[#FFF0F6] to-[#F5EEFF]" data-testid="week-hero">
        <header className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-60">Denna vecka</p>
            <h3 className="font-['Fredoka'] font-bold text-lg capitalize">{weekRange}</h3>
          </div>
          <span className="text-xs font-bold opacity-60">{weekItems.length} saker</span>
        </header>
        {weekItems.length === 0 ? (
          <p className="text-xs opacity-60 p-2 text-center">🌸 Tomt — fyll i något under Månadsöversikten</p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-[180px] overflow-y-auto">
            {weekItems.map((it) => {
              const wkLabel = WEEKDAYS_SV[(it.weekday.getDay() + 6) % 7];
              const catMeta = it.kind === 'calendar' ? (CATEGORY_META[it.category] || CATEGORY_META.other) : null;
              const bg = catMeta?.bg || (KIND_CHIP[it.kind]?.bg || '#fff');
              const border = catMeta?.border || (KIND_CHIP[it.kind]?.border || '#888');
              const emoji = catMeta?.emoji || (KIND_CHIP[it.kind]?.emoji || '');
              return (
                <li
                  key={`${it.id}-${it.dateKey}`}
                  className={`text-[11px] rounded px-2 py-1 border-l-2 truncate cursor-pointer hover:shadow-sm flex items-center gap-1 ${it.completed ? 'line-through opacity-50' : ''}`}
                  style={{ backgroundColor: bg, borderLeftColor: border }}
                  onClick={() => onDayClick(it.dateKey)}
                  data-testid={`week-hero-item-${it.id}`}
                  title={`${wkLabel} ${it.weekday.getDate()} · ${it.title}`}
                >
                  <span className="font-bold text-[9px] opacity-60 shrink-0 w-8">{wkLabel} {it.weekday.getDate()}</span>
                  <span className="shrink-0">{emoji}</span>
                  {it.time && <span className="font-bold shrink-0">{it.time}</span>}
                  <span className="truncate">{it.title}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Original 7-day grid */}
      <div className="neo-card p-2 bg-white" data-testid="week-view">
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const key = fmtDateKey(d);
            const isToday = isSameDay(d, today);
            const items = buildDayItems(key, data);
            return (
              <button
                key={key}
                onClick={() => onDayClick(key)}
                className={`rounded-md border p-1.5 text-left transition-all hover:shadow-md ${isToday ? 'border-[#FFC8DD] border-2 bg-[#FFF0F6]' : 'border-black/10 bg-white'}`}
                style={{ minHeight: 220 }}
                data-testid={`week-day-${key}`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[10px] font-bold opacity-60 uppercase">{WEEKDAYS_SV[i]}</span>
                  <span className={`text-lg font-bold ${isToday ? 'text-[#D06892]' : ''}`}>{d.getDate()}</span>
                </div>
                <div className="space-y-0.5 overflow-hidden">
                  {items.length === 0 ? <p className="text-[9px] opacity-40 italic">Tomt</p> :
                    items.slice(0, 8).map((it) => <InlineChip key={it.id} item={it} bujoMode={bujoMode} />)
                  }
                  {items.length > 8 && <p className="text-[9px] opacity-60 font-bold">+{items.length - 8} fler</p>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ---- YEAR VIEW (12 mini-months overview) ----
function YearView({ year, data, onMonthClick }) {
  const today = new Date();
  const counts = useMemo(() => {
    const map = {};
    (data.calendar || []).forEach((e) => {
      if (!e.occurrence_date) return;
      const d = new Date(e.starts_at);
      if (d.getFullYear() !== year) return;
      map[e.occurrence_date] = (map[e.occurrence_date] || 0) + 1;
    });
    return map;
  }, [data, year]);

  return (
    <div className="neo-card p-3 bg-white" data-testid="year-view">
      <h2 className="font-['Fredoka'] font-bold text-xl text-center mb-3">{year}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {MONTHS_SV.map((mName, m) => {
          const first = firstOfMonth(year, m);
          const last = lastOfMonth(year, m);
          const firstWeekday = (first.getDay() + 6) % 7;
          const daysInMonth = last.getDate();
          const cells = [];
          for (let i = 0; i < firstWeekday; i++) cells.push(null);
          for (let dd = 1; dd <= daysInMonth; dd++) cells.push(dd);
          let monthEventCount = 0;
          Object.keys(counts).forEach((k) => {
            if (k.startsWith(`${year}-${String(m + 1).padStart(2, '0')}`)) monthEventCount += counts[k];
          });
          const isCurrentMonth = today.getFullYear() === year && today.getMonth() === m;
          return (
            <button
              key={m}
              onClick={() => onMonthClick(m)}
              className={`rounded-lg border-2 p-2 text-left hover:shadow-md transition-all ${isCurrentMonth ? 'border-[#FFC8DD] bg-[#FFF0F6]' : 'border-black/10 bg-white'}`}
              data-testid={`year-month-${m}`}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className={`text-xs font-bold ${isCurrentMonth ? 'text-[#D06892]' : ''}`}>{mName}</span>
                {monthEventCount > 0 && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#CDB4DB] text-white">{monthEventCount}</span>
                )}
              </div>
              <div className="grid grid-cols-7 gap-[1px]">
                {['M', 'T', 'O', 'T', 'F', 'L', 'S'].map((w, i) => (
                  <div key={`h${i}`} className="text-[7px] font-bold opacity-40 text-center">{w}</div>
                ))}
                {cells.map((dd, i) => {
                  if (!dd) return <div key={`e${i}`} className="w-[14px] h-[14px]" />;
                  const k = `${year}-${String(m + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
                  const c = counts[k] || 0;
                  const isToday = today.getFullYear() === year && today.getMonth() === m && today.getDate() === dd;
                  let bg = '#ffffff', text = 'rgba(0,0,0,0.6)';
                  if (c > 0) {
                    if (c >= 4) { bg = '#D06892'; text = 'white'; }
                    else if (c >= 2) { bg = '#FFC8DD'; text = 'rgba(0,0,0,0.8)'; }
                    else { bg = '#FFE4EC'; text = 'rgba(0,0,0,0.8)'; }
                  }
                  return (
                    <div
                      key={`d${i}`}
                      className={`w-[14px] h-[14px] text-[7px] flex items-center justify-center rounded-sm ${isToday ? 'ring-1 ring-[#D06892]' : ''}`}
                      style={{ backgroundColor: bg, color: text, fontWeight: c > 0 ? 700 : 400 }}
                      title={c > 0 ? `${k} · ${c} saker` : k}
                    >
                      {dd}
                    </div>
                  );
                })}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---- Month tabs sidebar (YEARLY + JAN..DEC) ----
function MonthTabsSidebar({ year, activeMonth, view, onSelectYear, onSelectMonth }) {
  const MONTH_ABBR_SV = ['JAN', 'FEB', 'MAR', 'APR', 'MAJ', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEC'];
  // 12 nyanser av lila/ametyst — från ljus lavendel till djup amethyst, går runt över året
  const TAB_COLORS = [
    '#F5F0FF', // JAN — softest lavender mist
    '#EDE4FF', // FEB
    '#E0D1FF', // MAR
    '#D4BEFF', // APR
    '#C8AAFF', // MAJ
    '#B898FF', // JUN — mid lavender
    '#A285F0', // JUL
    '#9073DE', // AUG
    '#7C5FCC', // SEP
    '#6A4BBA', // OKT
    '#5837A8', // NOV
    '#4A2D8E', // DEC — deepest amethyst
  ];
  // Text contrast: dark for the lighter tabs, white for the deep ones
  const TAB_TEXT = (i) => i < 6 ? '#3D1A78' : '#FFFFFF';
  const today = new Date();
  return (
    <nav className="flex flex-col gap-0.5 py-1" data-testid="month-tabs">
      <button
        onClick={onSelectYear}
        className={`text-[10px] font-bold tracking-widest py-2 px-1 rounded-l-md writing-mode-vertical transition-all ${view === 'year' ? 'bg-black text-white' : 'bg-gray-800 text-white/80 hover:bg-black'}`}
        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', minHeight: 56 }}
        data-testid="tab-yearly"
        title={`Hela året ${year}`}
      >
        {year}
      </button>
      {MONTH_ABBR_SV.map((m, idx) => {
        const isActive = view !== 'year' && activeMonth === idx;
        const isCurrentMonth = today.getFullYear() === year && today.getMonth() === idx;
        return (
          <button
            key={idx}
            onClick={() => onSelectMonth(idx)}
            className={`text-[10px] font-bold tracking-widest py-2 px-1 rounded-l-md transition-all ${isActive ? 'shadow-md scale-[1.05] translate-x-[-4px]' : 'opacity-80 hover:opacity-100'}`}
            style={{
              backgroundColor: TAB_COLORS[idx],
              color: TAB_TEXT(idx),
              writingMode: 'vertical-rl',
              transform: isActive ? 'rotate(180deg) translateY(4px)' : 'rotate(180deg)',
              minHeight: 46,
              border: isCurrentMonth ? '2px solid #6A4BBA' : '1px solid rgba(74,45,142,0.18)',
            }}
            data-testid={`tab-month-${idx}`}
            title={MONTHS_SV[idx]}
          >
            {m}
          </button>
        );
      })}
    </nav>
  );
}


// ---- DAY VIEW (full day in main area) ----
function DayView({ date, data, onRefresh, bujoMode }) {
  const [editingEvent, setEditingEvent] = useState(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);
  const bumpTaskRefresh = () => setTaskRefreshKey((k) => k + 1);
  const items = useMemo(() => buildDayItems(date, data), [date, data]);
  const open = items.filter((i) => !i.completed);
  const done = items.filter((i) => i.completed);
  const d = fromDateKey(date);
  const label = d.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });

  const toggle = async (item) => {
    try {
      if (item.kind === 'schedule') await axios.patch(`${API}/schedule/${item.ref_id}`, { completed_today: !item.completed }, { withCredentials: true });
      else if (item.kind === 'cleaning') await axios.post(`${API}/cleaning/tasks/${item.ref_id}/${item.completed ? 'uncomplete' : 'complete'}`, {}, { withCredentials: true });
      else if (item.kind === 'calendar') {
        const url = item.completed ? `${API}/calendar/events/uncomplete` : `${API}/calendar/events/complete`;
        await axios.post(url, { event_id: item.ref_id, occurrence_date: item.occurrence_date }, { withCredentials: true });
      } else if (item.kind === 'task') await axios.patch(`${API}/tasks/${item.ref_id}`, { completed: !item.completed }, { withCredentials: true });
      else if (item.kind === 'tpl-schedule') await axios.post(`${API}/schedule-template/toggle`, { time: item.ref_id, date: item.dateKey || date, done: !item.completed }, { withCredentials: true });
      else if (item.kind === 'tpl-routine') await axios.post(`${API}/routines-template/toggle`, { item_id: item.ref_id, date: item.dateKey || date, done: !item.completed }, { withCredentials: true });
      onRefresh();
    } catch { toast.error('Kunde inte uppdatera'); }
  };
  const removeTask = async (item) => {
    if (!window.confirm('Ta bort?')) return;
    try { await axios.delete(`${API}/tasks/${item.ref_id}`, { withCredentials: true }); toast.success('Borttagen'); onRefresh(); }
    catch { toast.error('Kunde inte ta bort'); }
  };

  const isToday = fmtDateKey(new Date()) === date;
  const heroLabel = isToday ? 'Det här händer idag' : 'Den här dagen';

  return (
    <div className="neo-card p-3 bg-gradient-to-br from-[#FFF7E6] via-[#FFF0F6] to-[#F5EEFF]" data-testid="day-view">
      <header className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-60" data-testid="day-hero-label">{heroLabel}</p>
          <h3 className="font-['Fredoka'] font-bold text-xl capitalize">{label}</h3>
          <p className="text-[11px] opacity-60 mt-0.5">{open.length} kvar · {done.length} klara</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditingEvent({})} className="neo-btn px-3 py-1.5 bg-white text-sm font-bold flex items-center gap-1" data-testid="day-add-event"><Plus className="w-4 h-4" /> Kal-händelse</button>
          <button onClick={() => setShowQuickAdd(true)} className="neo-btn px-3 py-1.5 bg-white text-sm font-bold flex items-center gap-1" data-testid="day-add-task"><Plus className="w-4 h-4" /> Task / Tid</button>
        </div>
      </header>
      {items.length === 0 ? (
        <div className="neo-card p-4 bg-white/80 text-center"><p className="text-sm font-bold">🌸 Inget planerat</p></div>
      ) : (
        <>
          <ul className="space-y-2" data-testid="day-open-list">
            {open.map((it) => <DayItem key={it.id} item={it} onToggle={toggle} onDelete={removeTask} onEditEvent={setEditingEvent} bujoMode={bujoMode} />)}
          </ul>
          {done.length > 0 && (
            <details className="neo-card p-2 bg-white/70 mt-3" data-testid="day-done-section">
              <summary className="cursor-pointer text-xs font-bold opacity-70 px-2 py-1">✅ Klara ({done.length})</summary>
              <ul className="space-y-2 mt-2">{done.map((it) => <DayItem key={it.id} item={it} onToggle={toggle} onDelete={removeTask} onEditEvent={setEditingEvent} bujoMode={bujoMode} />)}</ul>
            </details>
          )}
        </>
      )}
      {editingEvent && <EventEditor date={date} existing={editingEvent.event_id ? editingEvent : null} onSave={onRefresh} onClose={() => setEditingEvent(null)} />}
      {showQuickAdd && <QuickAddMenu date={date} onAdded={onRefresh} onClose={() => setShowQuickAdd(false)} />}

      {/* Daily Planner (sitter under Dag, så som användaren förväntar) */}
      <div className="mt-4 neo-card p-2 bg-white" data-testid="day-daily-planner">
        <DailyPlanner date={date} refreshKey={taskRefreshKey} />
      </div>

      {/* Trafikljus 🟢🟡🔴 — tasks sorterade efter brådska */}
      <div className="mt-4 neo-card p-3 bg-gradient-to-br from-[#F0FFF4] via-[#FFFBEB] to-[#FFF1F2]" data-testid="day-traffic-lights">
        <p className="text-xs font-bold mb-2 flex items-center gap-1.5">🟢🟡🔴 Trafikljus — vad är brådskande?</p>
        <TaskList onTaskComplete={() => { onRefresh(); bumpTaskRefresh(); }} />
      </div>
    </div>
  );
}

// ---- Cleaning Manager (modal) ----
function CleaningManagerModal({ data, onClose, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [points, setPoints] = useState(10);
  const [busy, setBusy] = useState(false);
  const [filterFreq, setFilterFreq] = useState('all');

  const addTask = async (e) => {
    e.preventDefault(); if (!title.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/cleaning/tasks`, { title: title.trim(), frequency, points }, { withCredentials: true });
      toast.success('Städuppgift tillagd'); setTitle(''); setShowAdd(false); onRefresh();
    } catch { toast.error('Kunde inte lägga till'); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Ta bort?')) return;
    try { await axios.delete(`${API}/cleaning/tasks/${id}`, { withCredentials: true }); toast.success('Borttagen'); onRefresh(); }
    catch { toast.error('Kunde inte ta bort'); }
  };
  const toggleDone = async (t) => {
    try { await axios.post(`${API}/cleaning/tasks/${t.id}/${t.done_this_period ? 'uncomplete' : 'complete'}`, {}, { withCredentials: true }); onRefresh(); }
    catch { toast.error('Kunde inte uppdatera'); }
  };
  const filtered = (data.cleaningAll || []).filter((c) => filterFreq === 'all' || c.frequency === filterFreq);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="cleaning-manager-backdrop">
      <div className="neo-card bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cleaning-manager">
        <header className="sticky top-0 z-10 bg-[#B9FBC0] p-3 flex items-center justify-between border-b-2 border-black/10">
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2"><Sparkle className="w-5 h-5" /> Hantera städuppgifter</h3>
          <button onClick={onClose} className="p-1" data-testid="cleaning-manager-close"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-3 space-y-3">
          <div className="flex gap-1 flex-wrap">
            <button onClick={() => setFilterFreq('all')} className={`neo-btn text-xs px-2 py-1 ${filterFreq === 'all' ? 'bg-[#CDB4DB]' : 'bg-white'}`}>Alla</button>
            {Object.entries(FREQ_META).map(([k, v]) => (
              <button key={k} onClick={() => setFilterFreq(k)} className={`neo-btn text-xs px-2 py-1 ${filterFreq === k ? '' : 'bg-white'}`} style={filterFreq === k ? { backgroundColor: v.color } : {}} data-testid={`clean-filter-${k}`}>{v.label}</button>
            ))}
            <button onClick={() => setShowAdd((s) => !s)} className="neo-btn text-xs px-2 py-1 bg-[#B9FBC0] ml-auto" data-testid="cleaning-add-open"><Plus className="w-3 h-3 inline" /> Ny</button>
          </div>
          {showAdd && (
            <form onSubmit={addTask} className="neo-card p-2 bg-[#F5EEFF] space-y-2">
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="T.ex. Tvätta fönster" className="neo-input w-full text-sm" data-testid="cleaning-new-title" />
              <div className="flex gap-2">
                <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="neo-input text-sm flex-1" data-testid="cleaning-new-freq">
                  {Object.entries(FREQ_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <input type="number" min={1} max={200} value={points} onChange={(e) => setPoints(Number(e.target.value))} className="neo-input text-sm w-20" />
                <button type="submit" disabled={busy} className="neo-btn text-sm px-3 bg-[#B9FBC0] font-bold" data-testid="cleaning-new-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Spara'}</button>
              </div>
            </form>
          )}
          <ul className="space-y-1">
            {filtered.map((c) => {
              const meta = FREQ_META[c.frequency] || { label: c.frequency, color: '#eee' };
              return (
                <li key={c.id} className={`flex items-center gap-2 p-2 rounded-md border border-black/10 ${c.done_this_period ? 'opacity-50' : ''}`} style={{ backgroundColor: meta.color + '33' }} data-testid={`cleaning-item-${c.id}`}>
                  <button onClick={() => toggleDone(c)} className="shrink-0">{c.done_this_period ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5 opacity-60" />}</button>
                  <span className={`flex-1 text-sm ${c.done_this_period ? 'line-through' : ''}`}>{c.title}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: meta.color }}>{meta.label}</span>
                  <span className="text-[10px] opacity-60">+{c.points}p</span>
                  <button onClick={() => del(c.id)} className="opacity-40 hover:opacity-100 p-1" data-testid={`cleaning-del-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---- CLEANING COLUMNS (Daily / Weekly / Monthly) — sits below the calendar ----

// Extracted above CleaningColumns so it's stable across renders
// (avoids react/no-unstable-nested-components perf issue). Receives its
// dependencies as props instead of closing over parent state.
function CleaningColumn({ label, emoji, accent, items, resetText, testid, busyId, onToggle }) {
  const left = items.filter((c) => !c.done_this_period).length;
  const total = items.length;
  const pct = total ? Math.round(((total - left) / total) * 100) : 0;
  return (
    <div
      className="neo-card p-3 flex flex-col"
      style={{ background: accent.bg, borderColor: accent.border }}
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <h4 className="font-['Fredoka'] font-bold text-base flex items-center gap-1.5">
          <span className="text-xl">{emoji}</span> {label}
        </h4>
        <span className="text-[10px] font-bold opacity-70">{total - left}/{total}</span>
      </div>
      {/* Progress bar */}
      <div className="h-1.5 rounded-full bg-white/60 overflow-hidden mb-2">
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: accent.border }}
        />
      </div>
      <p className="text-[10px] italic opacity-60 mb-2">{resetText}</p>
      {items.length === 0 ? (
        <p className="text-[11px] opacity-50 italic flex-1 flex items-center justify-center py-4">Inga uppgifter just nu 🌸</p>
      ) : (
        <ul className="space-y-1 flex-1">
          {items.map((c) => (
            <li
              key={c.id}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-md bg-white/80 border border-black/5 ${c.done_this_period ? 'opacity-50' : ''}`}
              data-testid={`clean-col-item-${c.id}`}
            >
              <button
                onClick={() => onToggle(c)}
                disabled={busyId === c.id}
                className="shrink-0"
                data-testid={`clean-col-toggle-${c.id}`}
                aria-label={c.done_this_period ? `Ångra ${c.title}` : `Markera ${c.title} som klar`}
              >
                {busyId === c.id
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : c.done_this_period
                    ? <CheckCircle2 className="w-5 h-5 text-green-600 fill-green-200" />
                    : <Circle className="w-5 h-5 opacity-60" />}
              </button>
              <span className={`flex-1 text-xs leading-tight ${c.done_this_period ? 'line-through' : ''}`}>{c.title}</span>
              <span className="text-[9px] font-bold opacity-50 shrink-0">+{c.points || 0}p</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CleaningColumns({ data, onRefresh }) {
  const [busyId, setBusyId] = useState(null);
  const all = data.cleaningAll || [];

  const buckets = useMemo(() => {
    const today = new Date();
    const weekday = (today.getDay() + 6) % 7; // 0=Mon
    const daily = all.filter((c) => c.frequency === 'daily');
    const weekly = all.filter((c) => c.frequency === 'weekly').filter((c) => {
      // If task has days_of_week, only show on those days; else show all week
      if (!c.days_of_week || c.days_of_week.length === 0) return true;
      return c.days_of_week.includes(weekday);
    });
    const monthly = all.filter((c) => c.frequency === 'monthly');
    return { daily, weekly, monthly };
  }, [all]);

  const toggle = async (c) => {
    setBusyId(c.id);
    try {
      const action = c.done_this_period ? 'uncomplete' : 'complete';
      const res = await axios.post(`${API}/cleaning/tasks/${c.id}/${action}`, {}, { withCredentials: true });
      if (!c.done_this_period && res.data?.points_earned > 0) {
        toast.success(`+${res.data.points_earned}p ✨`);
      }
      onRefresh();
    } catch { toast.error('Kunde inte uppdatera'); }
    finally { setBusyId(null); }
  };


  return (
    <section className="neo-card p-3 bg-gradient-to-br from-[#FFF7E6] via-[#FFF0F6] to-[#F5EEFF]" data-testid="cleaning-columns">
      <header className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-60">Städrutiner</p>
          <h3 className="font-['Fredoka'] font-bold text-lg">Bocka av i din egen takt 🌷</h3>
        </div>
        <p className="text-[10px] opacity-60 italic max-w-md text-right">
          Listorna nollställs automatiskt — ingen press, ingen guilt.
        </p>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <CleaningColumn
          label="Dagligen"
          emoji="🌅"
          accent={{ bg: '#FFF8E1', border: '#E0A330' }}
          items={buckets.daily}
          resetText="Nollställs varje natt 🌙"
          testid="clean-col-daily"
          busyId={busyId}
          onToggle={toggle}
        />
        <CleaningColumn
          label="Veckovis"
          emoji="📅"
          accent={{ bg: '#E6F4EA', border: '#4DAA4D' }}
          items={buckets.weekly}
          resetText="Nollställs varje måndag morgon"
          testid="clean-col-weekly"
          busyId={busyId}
          onToggle={toggle}
        />
        <CleaningColumn
          label="Månadsvis"
          emoji="🗓️"
          accent={{ bg: '#F0E6F7', border: '#8B63C4' }}
          items={buckets.monthly}
          resetText="Nollställs den 1:a varje månad"
          testid="clean-col-monthly"
          busyId={busyId}
          onToggle={toggle}
        />
      </div>
    </section>
  );
}

// ---- Journal modal (BuJo) ----
function JournalModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="journal-modal-backdrop">
      <div className="neo-card bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="journal-modal">
        <header className="sticky top-0 z-10 bg-[#CDB4DB] p-3 flex items-center justify-between border-b-2 border-black/10">
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2"><BookOpen className="w-5 h-5" /> Journal</h3>
          <button onClick={onClose} className="p-1" data-testid="journal-modal-close"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-3"><BujoPage /></div>
      </div>
    </div>
  );
}

// ---- Upcoming events right column ----
function UpcomingEvents({ data, onDayClick, onEditEvent }) {
  const now = new Date();
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const horizon = new Date(today0); horizon.setDate(horizon.getDate() + 30);
  const [showAll, setShowAll] = useState(false);

  const upcoming = useMemo(() => {
    const list = [];
    (data.calendar || []).forEach((e) => {
      const dt = e.starts_at ? new Date(e.starts_at) : null;
      if (!dt || dt < today0 || dt > horizon) return;
      if (e.completed) return;
      // Filter: only "important" events appear by default; toggle to see all
      if (!showAll && !e.important) return;
      list.push({
        kind: 'calendar', dt, raw: e,
        title: e.title,
        category: e.category || 'other',
        time: e.all_day ? null : timeOf(e.starts_at),
        dateKey: e.occurrence_date,
        important: !!e.important,
        bujo_kind: e.bujo_kind || 'event',
      });
    });
    // External (synced) events — toggle controls whether they appear
    if (showAll) {
      const seen = new Set();
      (data.external || []).forEach((e) => {
        if (!e.occurrence_date) return;
        const dt = fromDateKey(e.occurrence_date);
        if (!dt || dt < today0 || dt > horizon) return;
        // De-dup multi-day occurrences sharing the same uid+dateKey
        const dedupKey = `${e.uid || e.id}-${e.occurrence_date}`;
        if (seen.has(dedupKey)) return;
        seen.add(dedupKey);
        list.push({
          kind: 'external', dt,
          title: e.title,
          time: e.all_day ? null : timeOf(e.start),
          dateKey: e.occurrence_date,
          feed_name: e.feed_name,
          feed_color: e.feed_color || '#7B5FBF',
          external: true,
        });
      });
    }
    list.sort((a, b) => a.dt - b.dt);
    return list.slice(0, 40);
  }, [data, today0, horizon, showAll]);

  // Group by date
  const grouped = useMemo(() => {
    const g = {};
    upcoming.forEach((it) => {
      const k = fmtDateKey(it.dt);
      (g[k] = g[k] || []).push(it);
    });
    return g;
  }, [upcoming]);

  return (
    <aside className="neo-card bg-white p-2 sticky top-2" data-testid="upcoming-events">
      <h3 className="font-['Fredoka'] font-bold text-sm flex items-center gap-1 mb-2 px-1">
        <Sparkle className="w-4 h-4" /> Kommande 30 dagar
        <span className="ml-auto text-[10px] opacity-60 font-bold">{upcoming.length}</span>
      </h3>
      <div className="flex items-center justify-between gap-1 px-1 mb-2">
        <p className="text-[10px] opacity-60 italic flex-1">
          {showAll ? 'Visar alla händelser' : 'Bara dina ! viktiga händelser'}
        </p>
        <button
          onClick={() => setShowAll((s) => !s)}
          className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-[#D06892]/30 hover:border-[#D06892] bg-[#FFE8F5] hover:bg-[#FFD9E6]"
          data-testid="upcoming-toggle-all"
        >
          {showAll ? 'Visa bara !' : 'Visa alla'}
        </button>
      </div>
      {upcoming.length === 0 ? (
        <p className="text-xs opacity-60 p-3 text-center">
          {showAll
            ? '🌸 Inget planerat framåt'
            : '✨ Inga viktiga händelser markerade — bocka i "Viktig" på dem du vill se här'}
        </p>
      ) : (
        <div className="space-y-2 max-h-[620px] overflow-y-auto pr-1">
          {Object.keys(grouped).sort().map((dateKey) => {
            const d = fromDateKey(dateKey);
            const isToday = isSameDay(d, new Date());
            const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
            const isTomorrow = isSameDay(d, tomorrow);
            const lbl = isToday ? 'Idag' : isTomorrow ? 'Imorgon' : d.toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' });
            const daysAway = Math.ceil((d - new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())) / 86400000);
            return (
              <div key={dateKey} className="border-l-2 pl-2" style={{ borderColor: isToday ? '#D06892' : isTomorrow ? '#E6C200' : '#CDB4DB' }}>
                <button
                  onClick={() => onDayClick(dateKey)}
                  className="flex items-baseline justify-between w-full text-left group"
                  data-testid={`upcoming-day-${dateKey}`}
                >
                  <span className="text-[10px] font-bold uppercase opacity-70 capitalize group-hover:opacity-100">{lbl}</span>
                  {!isToday && !isTomorrow && daysAway > 0 && <span className="text-[9px] opacity-50">{daysAway}d</span>}
                </button>
                <ul className="space-y-1 mt-1">
                  {grouped[dateKey].map((it, idx) => {
                    const isExternal = it.kind === 'external';
                    const catMeta = it.kind === 'calendar' ? (CATEGORY_META[it.category] || CATEGORY_META.other) : null;
                    const bg = isExternal ? (it.feed_color || '#7B5FBF') + '22' : (catMeta?.bg || '#eee');
                    const border = isExternal ? (it.feed_color || '#7B5FBF') : (catMeta?.border || '#888');
                    const bujoGlyph = isExternal ? '🍎' : (BUJO_SYMBOLS[it.bujo_kind || 'event']?.glyph || '▲');
                    return (
                      <li
                        key={idx}
                        className="text-[11px] rounded px-1.5 py-1 border-l-2 truncate cursor-pointer hover:shadow-sm transition-all flex items-center gap-1"
                        style={{ backgroundColor: bg, borderLeftColor: border }}
                        onClick={() => it.kind === 'calendar' && onEditEvent ? onEditEvent(it.raw) : onDayClick(dateKey)}
                        title={`${it.time ? it.time + ' · ' : ''}${it.title}${isExternal && it.feed_name ? ' · ' + it.feed_name : ''}`}
                        data-testid={`upcoming-item-${it.kind}-${idx}`}
                      >
                        {it.important && <span className="font-bold text-[#D06892] shrink-0">!</span>}
                        <span className="font-bold shrink-0">{bujoGlyph}</span>
                        {it.time && <span className="font-bold shrink-0">{it.time}</span>}
                        <span className="truncate">{it.title}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </aside>
  );
}

// ---- Main CalendarHubPage ----
export default function CalendarHubPage() {
  const [view, setView] = useState('month'); // year | month | week | day
  const [bujoMode, setBujoMode] = useState(() => {
    try { return localStorage.getItem('cal_bujo_mode') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('cal_bujo_mode', bujoMode ? '1' : '0'); } catch {/**/}
  }, [bujoMode]);
  const [spacious, setSpacious] = useState(() => {
    try { return localStorage.getItem('cal_spacious') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('cal_spacious', spacious ? '1' : '0'); } catch {/**/}
  }, [spacious]);
  const [studioOpen, setStudioOpen] = useState(false);
  const [activeTheme, setActiveTheme] = useState(null);
  const [themeBgB64, setThemeBgB64] = useState(null);

  const loadTheme = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cal-theme/active`, { withCredentials: true });
      setActiveTheme(r.data?.theme || null);
      if (r.data?.theme?.pattern === 'custom' && r.data.theme.custom_bg_asset_id) {
        try {
          const a = await axios.get(`${API}/cal-theme/asset/${r.data.theme.custom_bg_asset_id}`, { withCredentials: true });
          setThemeBgB64({ data: a.data.data_b64, mime: a.data.mime });
        } catch {/**/}
      } else { setThemeBgB64(null); }
    } catch {/**/}
  }, []);
  useEffect(() => { loadTheme(); }, [loadTheme]);
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => fmtDateKey(new Date()));
  const [data, setData] = useState({ tasks: [], schedule: [], calendar: [], cleaningAll: [], cleaningDaily: [], external: [] });
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayModal, setDayModal] = useState(null); // date string when open
  const [showJournal, setShowJournal] = useState(false);
  const [showCleaningMgr, setShowCleaningMgr] = useState(false);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [editingUpcomingEvent, setEditingUpcomingEvent] = useState(null);
  const [showRecap, setShowRecap] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [catFilter, setCatFilter] = useState(() => {
    try {
      const v = localStorage.getItem('calhub_cat_filter');
      return v ? new Set(JSON.parse(v)) : new Set();
    } catch { return new Set(); }
  });

  const toggleCatFilter = (cat) => {
    setCatFilter((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      try { localStorage.setItem('calhub_cat_filter', JSON.stringify(Array.from(next))); } catch { /* storage full */ }
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const y = cursor.getFullYear(), m = cursor.getMonth();
      // For year view fetch whole year; otherwise month ± 7 days
      let start, end, fromDate, toDate;
      if (view === 'year') {
        start = new Date(y, 0, 1).toISOString();
        end = new Date(y, 11, 31, 23, 59, 59).toISOString();
        fromDate = `${y}-01-01`; toDate = `${y}-12-31`;
      } else {
        const { start: mStart, end: mEnd } = isoMonthRange(y, m);
        start = new Date(new Date(mStart).getTime() - 7 * 86400000).toISOString();
        end = new Date(new Date(mEnd).getTime() + 7 * 86400000).toISOString();
        fromDate = start.split('T')[0]; toDate = end.split('T')[0];
      }
      // Track each call so we can surface specific failures (esp. calendar)
      const failures = [];
      const trackCatch = (label) => (err) => {
        failures.push({ label, status: err?.response?.status, msg: err?.response?.data?.detail || err?.message });
        return { data: label === 'calendar' ? { items: [] }
                     : label === 'tasks' || label === 'schedule' ? []
                     : label === 'cleaningAll' || label === 'cleaningDaily' ? { items: [] }
                     : label === 'tplSchedule' ? { blocks: {}, completions_by_date: {} }
                     : label === 'tplRoutines' ? { items: [], completions_by_date: {} }
                     : label === 'synced' ? { events: [] } : {} };
      };
      const [tasks, schedule, calendar, cleaningAll, cleaningDaily, tplSchedule, tplRoutines, synced] = await Promise.all([
        axios.get(`${API}/tasks`, { withCredentials: true }).catch(trackCatch('tasks')),
        axios.get(`${API}/schedule`, { withCredentials: true }).catch(trackCatch('schedule')),
        axios.get(`${API}/calendar/events`, { withCredentials: true, params: { from_date: start, to_date: end } }).catch(trackCatch('calendar')),
        axios.get(`${API}/cleaning/tasks`, { withCredentials: true }).catch(trackCatch('cleaningAll')),
        axios.get(`${API}/cleaning/today`, { withCredentials: true }).catch(trackCatch('cleaningDaily')),
        axios.get(`${API}/schedule-template/range`, { withCredentials: true, params: { from_date: fromDate, to_date: toDate } }).catch(trackCatch('tplSchedule')),
        axios.get(`${API}/routines-template/range`, { withCredentials: true, params: { from_date: fromDate, to_date: toDate } }).catch(trackCatch('tplRoutines')),
        axios.get(`${API}/calendar-sync/events`, { withCredentials: true, params: { start, end } }).catch(trackCatch('synced')),
      ]);

      // Surface a banner if anything important failed (especially calendar events)
      if (failures.length > 0) {
        const calFail = failures.find((f) => f.label === 'calendar');
        if (calFail) {
          setLoadError({
            type: 'calendar',
            message: 'Vi kunde inte hämta dina kalenderhändelser just nu — dina händelser är SPARADE, det är bara visningen som hängde sig. Försök igen om en stund.',
            details: `${calFail.status || ''} ${calFail.msg || ''}`.trim(),
          });
        } else {
          setLoadError({
            type: 'partial',
            message: `Vissa kalenderdata kunde inte hämtas (${failures.map((f) => f.label).join(', ')}). Dina händelser är säkra.`,
            details: '',
          });
        }
      }

      // Expand multi-day external events into one occurrence per day
      const externalOccurrences = [];
      (synced.data?.events || []).forEach((ev) => {
        try {
          const s = new Date(ev.start);
          const e = ev.end ? new Date(ev.end) : s;
          // For all-day events, end is exclusive in iCal — back off 1ms
          const endInclusive = ev.all_day && e.getTime() > s.getTime()
            ? new Date(e.getTime() - 1)
            : e;
          const cursor = new Date(s.getFullYear(), s.getMonth(), s.getDate());
          const last = new Date(endInclusive.getFullYear(), endInclusive.getMonth(), endInclusive.getDate());
          let safety = 0;
          while (cursor.getTime() <= last.getTime() && safety < 60) {
            externalOccurrences.push({ ...ev, occurrence_date: fmtDateKey(cursor) });
            cursor.setDate(cursor.getDate() + 1);
            safety++;
          }
        } catch {/* skip malformed */}
      });

      setData({
        tasks: Array.isArray(tasks.data) ? tasks.data : [],
        schedule: Array.isArray(schedule.data) ? schedule.data : [],
        calendar: calendar.data?.items || [],
        cleaningAll: cleaningAll.data?.items || [],
        cleaningDaily: cleaningDaily.data?.items || [],
        tplSchedule: tplSchedule.data || { blocks: {}, completions_by_date: {} },
        tplRoutines: tplRoutines.data || { items: [], completions_by_date: {} },
        external: externalOccurrences,
      });
    } catch { toast.error('Kunde inte ladda kalendern'); }
    finally { setLoading(false); }
  }, [cursor, view]);

  useEffect(() => { load(); }, [load]);

  // Apply category filter to calendar events (empty filter = show all)
  const filteredData = useMemo(() => {
    if (!catFilter.size) return data;
    return {
      ...data,
      calendar: (data.calendar || []).filter((e) => catFilter.has(e.category || 'other')),
    };
  }, [data, catFilter]);

  const headerLabel = useMemo(() => {
    if (view === 'year') return `${cursor.getFullYear()}`;
    if (view === 'month') return `${MONTHS_SV[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === 'week') {
      const ws = startOfWeek(cursor);
      const we = new Date(ws); we.setDate(ws.getDate() + 6);
      return `${ws.getDate()} ${MONTHS_SV[ws.getMonth()].slice(0, 3).toLowerCase()} — ${we.getDate()} ${MONTHS_SV[we.getMonth()].slice(0, 3).toLowerCase()} ${we.getFullYear()}`;
    }
    return fromDateKey(selectedDate).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }, [view, cursor, selectedDate]);

  const goto = (delta) => {
    const d = new Date(cursor);
    if (view === 'year') d.setFullYear(d.getFullYear() + delta);
    else if (view === 'month') d.setMonth(d.getMonth() + delta);
    else if (view === 'week') d.setDate(d.getDate() + 7 * delta);
    else d.setDate(d.getDate() + delta);
    setCursor(d);
    if (view === 'day') setSelectedDate(fmtDateKey(d));
  };
  const gotoToday = () => { const d = new Date(); setCursor(d); setSelectedDate(fmtDateKey(d)); };

  const onDayClick = (dateKey) => {
    if (view === 'day') return;
    setSelectedDate(dateKey);
    setDayModal(dateKey);
  };

  return (
    <div className="space-y-3 relative" data-testid="calendar-hub-page">
      {/* Top Toolbar */}
      <header className="neo-card p-3 bg-gradient-to-br from-[#E8F0FF] via-[#F5EEFF] to-[#FFE8F5] flex items-center gap-2 flex-wrap" data-testid="calhub-header">
        <div className="flex items-center gap-1">
          <button onClick={() => goto(-1)} className="neo-btn p-2 bg-white" data-testid="calhub-prev"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => goto(1)} className="neo-btn p-2 bg-white" data-testid="calhub-next"><ChevronRight className="w-5 h-5" /></button>
          <button onClick={gotoToday} className="neo-btn px-3 py-1.5 bg-white text-xs font-bold" data-testid="calhub-today">Idag</button>
        </div>
        <h2 className="font-['Fredoka'] font-bold text-lg sm:text-xl flex-1 text-center capitalize">{headerLabel}</h2>
        <div className="flex items-center gap-1" data-testid="calhub-view-switch">
          <button onClick={() => setView('year')} className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${view === 'year' ? 'bg-[#FFC8DD]' : 'bg-white'}`} data-testid="view-year"><CalendarDays className="w-4 h-4" /> År</button>
          <button onClick={() => setView('month')} className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${view === 'month' ? 'bg-[#FFC8DD]' : 'bg-white'}`} data-testid="view-month"><CalendarDays className="w-4 h-4" /> Månad</button>
          <button onClick={() => setView('week')} className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${view === 'week' ? 'bg-[#FFC8DD]' : 'bg-white'}`} data-testid="view-week"><CalendarRange className="w-4 h-4" /> Vecka</button>
          <button onClick={() => { setView('day'); setSelectedDate(fmtDateKey(cursor)); }} className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${view === 'day' ? 'bg-[#FFC8DD]' : 'bg-white'}`} data-testid="view-day"><CalIcon className="w-4 h-4" /> Dag</button>
          <button
            onClick={() => setBujoMode((v) => !v)}
            data-testid="bujo-mode-toggle"
            title="Bujo-läge: pastell-papper, klistermärken, sticky notes"
            className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ml-1 ${bujoMode ? 'bg-gradient-to-r from-pink-200 via-rose-200 to-violet-200' : 'bg-white'}`}
          >
            ✨ Bujo
          </button>
          <button
            onClick={() => setStudioOpen(true)}
            data-testid="theme-studio-open"
            title="Tema-studio: bygg och spara egna teman"
            className="neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 bg-gradient-to-r from-violet-200 to-pink-200"
          >
            💎 Tema
          </button>
          <button
            onClick={() => setSpacious((v) => !v)}
            data-testid="cal-spacious-toggle"
            title="Mer luft: dubbla cellhöjden så du har mer plats att skriva och rita"
            className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${spacious ? 'bg-[#A2D2FF]' : 'bg-white'}`}
          >
            📐 {spacious ? 'Kompakt' : 'Mer luft'}
          </button>
        </div>
        <button onClick={() => setShowNewEvent(true)} className="neo-btn px-3 py-1.5 bg-[#B9FBC0] text-xs font-bold flex items-center gap-1" data-testid="calhub-add"><Plus className="w-4 h-4" /> Ny</button>
      </header>

      {/* Error banner — when calendar API fails, reassure user their data is safe */}
      {loadError && (
        <div className={`neo-card p-3 flex items-start gap-2 ${loadError.type === 'calendar' ? 'bg-[#FFE2E2] border-red-200' : 'bg-[#FFF5DC] border-yellow-200'}`} data-testid="calhub-load-error">
          <div className="text-2xl shrink-0">{loadError.type === 'calendar' ? '⚠️' : '🌸'}</div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">{loadError.type === 'calendar' ? 'Något gick fel — men inget är borta' : 'Delvis laddning'}</p>
            <p className="text-xs opacity-80 mt-0.5">{loadError.message}</p>
            {loadError.details && <p className="text-[10px] opacity-50 italic mt-1">{loadError.details}</p>}
          </div>
          <button onClick={load} className="neo-btn px-2 py-1 bg-white text-xs font-bold shrink-0" data-testid="calhub-retry">Försök igen</button>
        </div>
      )}

      {loading && <div className="neo-card p-6 bg-white text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}

      {/* Main calendar area + upcoming events column */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3" data-testid="calhub-layout">
        <div className="min-w-0 flex gap-1" data-testid="calhub-main">
          <div className="flex-1 min-w-0 space-y-3">
            {(() => {
              const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
              const themeStyle = calThemeStyle(activeTheme);
              const innerView = (
                <>
                  {view === 'year' && <YearView year={cursor.getFullYear()} data={filteredData} onMonthClick={(m) => { const d = new Date(cursor); d.setMonth(m); setCursor(d); setView('month'); }} />}
                  {view === 'month' && <MonthView year={cursor.getFullYear()} month={cursor.getMonth()} data={filteredData} onDayClick={onDayClick} onCreated={load} bujoMode={bujoMode} themeStyle={themeStyle} spacious={spacious} />}
                  {view === 'week' && <WeekView anchor={cursor} data={filteredData} onDayClick={onDayClick} bujoMode={bujoMode} />}
                  {view === 'day' && <DayView date={selectedDate} data={filteredData} onRefresh={load} bujoMode={bujoMode} />}
                </>
              );
              // Theme-studio look (independent of bujo mode)
              const hasTheme = !!activeTheme;
              if (hasTheme) {
                const t = activeTheme;
                const isTpl = !!t.template_mode && t.pattern === 'custom';
                const bg = (t.pattern === 'custom' && themeBgB64)
                  ? `url(data:${themeBgB64.mime};base64,${themeBgB64.data}) center/cover`
                  : `linear-gradient(${t.gradient_dir || '135deg'}, ${t.gradient_from} 0%, ${t.gradient_via} 50%, ${t.gradient_to} 100%)`;
                const cornerEmoji = isTpl ? '' : { gem: '💎', flower: '🌸', butterfly: '🦋', heart: '💖', none: '' }[t.corner_decor || 'gem'];
                const fontClass = t.title_font === 'script' ? 'italic font-serif'
                                : t.title_font === 'sans'   ? 'font-sans'
                                : t.title_font === 'display' ? 'uppercase font-extrabold'
                                : 'font-serif';
                return (
                  <div className={`relative rounded-3xl shadow-inner ${isTpl ? 'p-0' : 'p-3 lg:p-5'}`} style={{ background: bg }} data-testid="theme-wrapper">
                    {cornerEmoji && (
                      <div className="absolute -top-2 -right-2 text-3xl pointer-events-none select-none drop-shadow-lg" aria-hidden>{cornerEmoji}</div>
                    )}
                    {!isTpl && (
                      <div className="text-center py-2 mb-2 select-none">
                        <h2 className={`text-2xl tracking-[0.3em] inline-block ${fontClass}`} style={{ color: t.accent }}>{t.title}</h2>
                        {t.subtitle && <div className="text-[10px] tracking-widest italic mt-0.5" style={{ color: t.accent_soft }}>{t.subtitle}</div>}
                      </div>
                    )}
                    {bujoMode ? (
                      <div>
                        <div className="grid grid-cols-1 xl:grid-cols-[180px_1fr_180px] gap-3">
                          <div className="hidden xl:block"><BujoSidebar month={monthKey} side="left" themeNotes={t.sticky_notes} /></div>
                          <div className="min-w-0">{innerView}</div>
                          <div className="hidden xl:block"><BujoSidebar month={monthKey} side="right" themeNotes={t.sticky_notes} /></div>
                        </div>
                        <div className="xl:hidden mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <BujoSidebar month={monthKey} side="left" themeNotes={t.sticky_notes} />
                          <BujoSidebar month={monthKey} side="right" themeNotes={t.sticky_notes} />
                        </div>
                      </div>
                    ) : innerView}
                  </div>
                );
              }
              if (!bujoMode) return innerView;
              return (
                <BujoPaperBg className="p-3 lg:p-5">
                  <div className="grid grid-cols-1 xl:grid-cols-[180px_1fr_180px] gap-3">
                    <div className="hidden xl:block"><BujoSidebar month={monthKey} side="left" /></div>
                    <div className="min-w-0">{innerView}</div>
                    <div className="hidden xl:block"><BujoSidebar month={monthKey} side="right" /></div>
                  </div>
                  {/* On small screens stack sidebars BELOW */}
                  <div className="xl:hidden mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <BujoSidebar month={monthKey} side="left" />
                    <BujoSidebar month={monthKey} side="right" />
                  </div>
                </BujoPaperBg>
              );
            })()}
          </div>
          <MonthTabsSidebar
            year={cursor.getFullYear()}
            activeMonth={cursor.getMonth()}
            view={view}
            onSelectYear={() => setView('year')}
            onSelectMonth={(m) => {
              const d = new Date(cursor); d.setMonth(m);
              setCursor(d);
              if (view === 'year') setView('month');
              if (view === 'day') setSelectedDate(fmtDateKey(d));
            }}
          />
        </div>
        <UpcomingEvents
          data={filteredData}
          onDayClick={(d) => { setSelectedDate(d); setDayModal(d); }}
          onEditEvent={(ev) => setEditingUpcomingEvent(ev)}
        />
      </div>

      {/* Cleaning routines — separated from calendar to reduce ADHD overload */}
      <CleaningColumns data={data} onRefresh={load} />

      {/* Category filter + legend */}
      <div className="neo-card p-2 bg-white/70" data-testid="calhub-category-filter">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[10px] font-bold uppercase opacity-60 mr-1">Kategorier:</span>
          {Object.entries(CATEGORY_META).map(([k, v]) => {
            const active = catFilter.has(k);
            const anyActive = catFilter.size > 0;
            return (
              <button
                key={k}
                onClick={() => toggleCatFilter(k)}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full border-2 transition-all ${active ? 'ring-2 ring-[#D06892]' : ''} ${anyActive && !active ? 'opacity-40' : ''}`}
                style={{ backgroundColor: v.bg, borderColor: v.border }}
                data-testid={`filter-cat-${k}`}
                title={`Visa bara ${v.label}`}
              >
                {v.emoji} {v.label}
              </button>
            );
          })}
          {catFilter.size > 0 && (
            <button
              onClick={() => { setCatFilter(new Set()); try { localStorage.setItem('calhub_cat_filter', '[]'); } catch { /* storage full */ } }}
              className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black/30 bg-white ml-auto"
              data-testid="filter-cat-clear"
            >
              ✕ Visa alla
            </button>
          )}
        </div>
        <p className="text-[9px] opacity-50 mt-1 italic">Bara dina kalenderhändelser visas — du styr helt själv 💗</p>
        <details className="mt-2 group" data-testid="bujo-legend">
          <summary className="cursor-pointer text-xs font-bold text-[#D06892] hover:text-[#A0496A] transition-colors flex items-center gap-1 select-none">
            <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
            <span>📐 BuJo-nycklar (klicka för att visa)</span>
          </summary>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 p-2 bg-[#FFF8FB] rounded-md border border-[#D06892]/20">
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-base font-bold w-6 text-center">▲</span>
              <span><b>Händelse</b> — något som händer</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-base font-bold w-6 text-center">☐</span>
              <span><b>Att göra</b> — sak att kryssa av</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-base font-bold w-6 text-center">•</span>
              <span><b>Anteckning</b> — tanke att minnas</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-base font-bold w-6 text-center text-[#D06892]">!</span>
              <span><b>Viktig</b> — visas i Kommande 30 dagar</span>
            </div>
          </div>
        </details>
      </div>

      {/* Footer utilities */}
      <div className="flex gap-2 flex-wrap pt-1" data-testid="calhub-footer">
        <button onClick={() => setShowRecap(true)} className="neo-btn px-3 py-1.5 bg-[#C8B6FF] text-xs font-bold flex items-center gap-1" data-testid="open-recap">
          <CalendarDays className="w-4 h-4" /> Månadsrapport
        </button>
        <button onClick={() => setShowTemplates(true)} className="neo-btn px-3 py-1.5 bg-[#A2D2FF] text-xs font-bold flex items-center gap-1" data-testid="open-week-templates">
          <CalendarRange className="w-4 h-4" /> Veckomallar
        </button>
        <button onClick={() => setShowJournal(true)} className="neo-btn px-3 py-1.5 bg-[#CDB4DB] text-xs font-bold flex items-center gap-1" data-testid="open-journal">
          <BookOpen className="w-4 h-4" /> Journal
        </button>
        <button onClick={() => setShowCleaningMgr(true)} className="neo-btn px-3 py-1.5 bg-[#B9FBC0] text-xs font-bold flex items-center gap-1" data-testid="open-cleaning-mgr">
          <Sparkle className="w-4 h-4" /> Hantera städuppgifter
        </button>
      </div>

      {/* 🌷 FLOATING ACTION BUTTON — alltid synlig, alltid hittbar */}
      <button
        onClick={() => setShowNewEvent(true)}
        className="fixed bottom-5 right-5 z-[220] w-16 h-16 rounded-full shadow-2xl bg-gradient-to-br from-[#FFC8DD] to-[#D06892] text-white flex flex-col items-center justify-center font-bold border-4 border-white hover:scale-110 active:scale-95 transition-transform"
        data-testid="calendar-fab"
        title="Lägg till händelse"
        aria-label="Lägg till händelse"
      >
        <Plus className="w-6 h-6" />
        <span className="text-[8px] leading-none mt-0.5">NY</span>
      </button>

      {/* Modals */}
      {dayModal && <DayDetailModal date={dayModal} data={filteredData} onClose={() => setDayModal(null)} onRefresh={load} bujoMode={bujoMode} />}
      {showJournal && <JournalModal onClose={() => setShowJournal(false)} />}
      {showCleaningMgr && <CleaningManagerModal data={data} onClose={() => setShowCleaningMgr(false)} onRefresh={load} />}
      {showTemplates && <WeekTemplatesModal onClose={() => setShowTemplates(false)} onApplied={load} />}
      {showNewEvent && <EventEditor date={fmtDateKey(new Date())} existing={null} onSave={load} onClose={() => setShowNewEvent(false)} />}
      {editingUpcomingEvent && (
        <EventEditor
          date={editingUpcomingEvent.starts_at ? fmtDateKey(new Date(editingUpcomingEvent.starts_at)) : fmtDateKey(new Date())}
          existing={editingUpcomingEvent}
          onSave={load}
          onClose={() => setEditingUpcomingEvent(null)}
        />
      )}
      {showRecap && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowRecap(false)} data-testid="recap-modal-backdrop">
          <div className="neo-card bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="recap-modal">
            <header className="sticky top-0 z-10 bg-[#C8B6FF] p-3 flex items-center justify-between border-b-2 border-black/10">
              <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2"><CalendarDays className="w-5 h-5" /> Månadsrapport</h3>
              <button onClick={() => setShowRecap(false)} className="p-1" data-testid="recap-modal-close"><X className="w-5 h-5" /></button>
            </header>
            <div className="p-3"><MonthlyRecap /></div>
          </div>
        </div>
      )}
      {studioOpen && <ThemeStudio onClose={() => setStudioOpen(false)} onThemeChanged={loadTheme} />}
    </div>
  );
}
